import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Megaphone, Camera, Calculator, ShieldCheck, Money, Warning, Pause, Play,
} from '@phosphor-icons/react';
import { api } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { AdminPageHeader, AdminKpi, AdminKpiStrip } from '../../components/admin/AdminPageHeader';
import { cn } from '../../lib/utils';

const money = (v) => `€${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const STEPS = [
  {
    key: 'draft',
    label: 'Declare',
    icon: Megaphone,
    statuses: ['draft'],
    title: 'Review and declare',
    help: 'Check the instrument, amount and record date. Declaring announces the distribution - nothing is paid yet.',
    cta: 'Declare distribution',
    action: 'declare',
  },
  {
    key: 'declared',
    label: 'Record date',
    icon: Camera,
    statuses: ['declared'],
    title: 'Freeze the cap table',
    help: 'We snapshot who held units at the record date. Later cap table changes will not affect this distribution.',
    cta: 'Take record-date snapshot',
    action: 'take-snapshot',
  },
  {
    key: 'record_date_set',
    label: 'Calculate',
    icon: Calculator,
    statuses: ['record_date_set'],
    title: 'Calculate entitlements',
    help: 'We first split the pot between Flippex and the investors (based on the share of the asset investor capital funded), then allocate the investor share pro-rata across eligible units.',
    cta: 'Calculate entitlements',
    action: 'calculate',
  },
  {
    key: 'calculated',
    label: 'Approve',
    icon: ShieldCheck,
    statuses: ['calculated'],
    title: 'Approve for payment',
    help: 'Approving locks the amounts and opens payments. Review the per-investor preview below first.',
    cta: 'Approve for payment',
    action: 'approve',
  },
  {
    key: 'approved',
    label: 'Pay',
    icon: Money,
    statuses: ['approved', 'partially_paid', 'paid', 'completed'],
    title: 'Record payments',
    help: 'Record each payout as it leaves the bank. Investors see the amount in their Returns and Activity.',
    cta: null,
    action: null,
  },
];

const stepIndexFor = (status) => {
  const i = STEPS.findIndex((s) => s.statuses.includes(status));
  return i === -1 ? 0 : i;
};

const Stepper = ({ current }) => (
  <div className="flex items-center gap-1.5 overflow-x-auto" data-testid="distribution-stepper">
    {STEPS.map((step, i) => {
      const Icon = step.icon;
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={step.key}>
          <div
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium whitespace-nowrap',
              active
                ? 'bg-primary text-white border-primary'
                : done
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-white text-foreground-muted border-black/[0.08]'
            )}
            data-testid={`step-${step.key}`}
          >
            {done ? <Check size={12} weight="bold" /> : <Icon size={13} weight="fill" />}
            {step.label}
          </div>
          {i < STEPS.length - 1 && <span className="h-px w-4 bg-black/10 flex-shrink-0" />}
        </React.Fragment>
      );
    })}
  </div>
);

const AdminDistributionWizardPage = () => {
  const { distributionId } = useParams();
  const navigate = useNavigate();
  const [dist, setDist] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [parties, setParties] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', payment_reference: '', payment_method: 'bank_transfer' });
  const [holdTarget, setHoldTarget] = useState(null);
  const [holdReason, setHoldReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [distRes, entRes, partyRes] = await Promise.all([
        api.get(`/admin/v2/distributions/${distributionId}`),
        api.get(`/admin/v2/distributions/${distributionId}/entitlements`).catch(() => ({ data: { entitlements: [] } })),
        api.get('/admin/v2/parties').catch(() => ({ data: { parties: [] } })),
      ]);
      setDist(distRes.data.distribution || distRes.data);
      setEntitlements(entRes.data.entitlements || []);
      const map = {};
      (partyRes.data.parties || []).forEach((p) => { map[p.id] = p.display_name || p.code; });
      setParties(map);
    } catch (err) {
      toast.error('That distribution could not be found');
      navigate('/admin/v2/distributions', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [distributionId, navigate]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (action) => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/v2/distributions/${distributionId}/${action}`);
      toast.success(res.data.message || 'Done');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const openPay = (ent) => {
    const remaining = Number(ent.net_amount || 0) - Number(ent.paid_amount || 0);
    setPayForm({ amount: remaining.toFixed(2), payment_reference: '', payment_method: 'bank_transfer' });
    setPayTarget(ent);
  };

  const submitPay = async () => {
    if (!payForm.payment_reference.trim()) {
      toast.error('Add a payment reference so the payout can be traced');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/v2/distributions/entitlements/${payTarget.id}/pay`, {
        amount: String(payForm.amount),
        payment_reference: payForm.payment_reference.trim(),
        payment_method: payForm.payment_method,
      });
      toast.success('Payment recorded');
      setPayTarget(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setBusy(false);
    }
  };

  const submitHold = async () => {
    if (holdReason.trim().length < 5) {
      toast.error('Give a reason of at least 5 characters');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/v2/distributions/entitlements/${holdTarget.id}/hold`, { reason: holdReason.trim() });
      toast.success('Entitlement on hold');
      setHoldTarget(null);
      setHoldReason('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hold entitlement');
    } finally {
      setBusy(false);
    }
  };

  const releaseHold = async (ent) => {
    setBusy(true);
    try {
      await api.post(`/admin/v2/distributions/entitlements/${ent.id}/release-hold`);
      toast.success('Hold released');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release hold');
    } finally {
      setBusy(false);
    }
  };

  const current = useMemo(() => stepIndexFor(dist?.status), [dist]);
  const step = STEPS[current];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-9 w-9 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (!dist) {
    return (
      <div className="max-w-[1200px] py-16 text-center">
        <p className="text-sm font-medium">Distribution not found</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/admin/v2/distributions')}>
          Back to distributions
        </Button>
      </div>
    );
  }

  const paid = Number(dist.total_paid || 0);
  const total = Number(dist.total_amount || 0);
  const progress = total > 0 ? Math.min((paid / total) * 100, 100) : 0;
  const allPaid = entitlements.length > 0 && entitlements.every((e) => e.status === 'paid');

  return (
    <div className="max-w-[1200px] space-y-6" data-testid="distribution-wizard-page">
      <button
        onClick={() => navigate('/admin/v2/distributions')}
        className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-slate-900"
        data-testid="wizard-back"
      >
        <ArrowLeft size={15} /> Distributions
      </button>

      <AdminPageHeader
        eyebrow={dist.code}
        title={dist.description || `${(dist.distribution_type || '').replace(/_/g, ' ')} distribution`}
        subtitle={`${dist.security_name || dist.security_code || 'Instrument'} · record date ${shortDate(dist.record_date)}`}
        actions={dist.status !== 'cancelled' && paid === 0 ? (
          <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} data-testid="cancel-distribution-btn">
            Cancel distribution
          </Button>
        ) : null}
      />

      <Stepper current={current} />

      <AdminKpiStrip>
        <AdminKpi label="Total amount" value={money(total)} />
        <AdminKpi label="Eligible units" value={dist.units_eligible?.toLocaleString() || '—'} />
        <AdminKpi label="Holders" value={dist.holders_count || '—'} />
        <AdminKpi label="Per unit" value={dist.per_unit_amount ? money(dist.per_unit_amount) : '—'} />
        <AdminKpi
          label="To investors"
          value={dist.investor_amount ? `${money(dist.investor_amount)} · ${Number(dist.investor_share_pct).toFixed(1)}%` : '—'}
        />
        <AdminKpi
          label="To Flippex"
          value={dist.flippex_amount !== undefined && dist.flippex_amount !== null ? `${money(dist.flippex_amount)} · ${Number(dist.flippex_share_pct || 0).toFixed(1)}%` : '—'}
        />
        <AdminKpi label="Paid" value={`${money(paid)} · ${progress.toFixed(0)}%`} />
      </AdminKpiStrip>

      {/* Current step */}
      <Card className="bg-white rounded-xl border border-black/[0.06] p-5 md:p-6" data-testid="wizard-current-step">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">
              Step {current + 1} of {STEPS.length}
            </p>
            <h2 className="text-base md:text-lg font-heading mt-1">{step.title}</h2>
            <p className="text-sm text-foreground-muted mt-1 max-w-2xl">{step.help}</p>
          </div>
          {step.cta && (
            <Button onClick={() => runAction(step.action)} disabled={busy} data-testid="wizard-primary-cta">
              {busy ? 'Working…' : step.cta}
            </Button>
          )}
          {!step.cta && allPaid && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
              <Check size={15} weight="bold" /> All investors paid
            </span>
          )}
        </div>

        {current === 0 && (
          <div className="mt-5 pt-5 border-t border-black/[0.05] grid gap-4 sm:grid-cols-3 text-sm">
            <div>
              <p className="text-xs text-foreground-muted">Type</p>
              <p className="font-medium capitalize mt-0.5">{(dist.distribution_type || '').replace(/_/g, ' ')}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-muted">Record date</p>
              <p className="font-medium mt-0.5">{shortDate(dist.record_date)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-muted">Payment date</p>
              <p className="font-medium mt-0.5">{shortDate(dist.payment_date)}</p>
            </div>
          </div>
        )}
      </Card>

      {/* Per-investor preview / payments */}
      {entitlements.length > 0 && (
        <Card className="bg-white rounded-xl border border-black/[0.06]" data-testid="entitlements-panel">
          <div className="px-5 py-3.5 border-b border-black/[0.05] flex items-center justify-between">
            <h3 className="text-sm font-medium">
              {current >= 4 ? 'Payments' : 'What each investor receives'}
            </h3>
            <span className="text-xs text-foreground-muted">{entitlements.length} investors</span>
          </div>
          <div className="divide-y divide-black/[0.05]">
            {entitlements.map((ent) => {
              const remaining = Number(ent.net_amount || 0) - Number(ent.paid_amount || 0);
              return (
                <div
                  key={ent.id}
                  className="px-5 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-2 justify-between"
                  data-testid={`entitlement-row-${ent.id}`}
                >
                  <div className="min-w-[180px]">
                    <p className="text-sm font-medium">{parties[ent.investor_id] || ent.investor_id}</p>
                    <p className="text-xs text-foreground-muted tabular-nums">
                      {ent.units_held?.toLocaleString()} units × {money(ent.per_unit_amount)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium tabular-nums">{money(ent.net_amount)}</p>
                    <p className="text-xs text-foreground-muted tabular-nums">
                      {ent.status === 'paid'
                        ? 'Paid in full'
                        : Number(ent.paid_amount) > 0
                          ? `${money(ent.paid_amount)} paid · ${money(remaining)} left`
                          : ent.status === 'on_hold'
                            ? `On hold — ${ent.hold_reason}`
                            : 'Not paid yet'}
                    </p>
                  </div>
                  {current >= 4 && (
                    <div className="flex items-center gap-2">
                      {ent.status === 'on_hold' ? (
                        <Button size="sm" variant="outline" onClick={() => releaseHold(ent)} disabled={busy} data-testid={`release-hold-${ent.id}`}>
                          <Play size={14} className="mr-1.5" /> Release hold
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => openPay(ent)}
                            disabled={busy || ent.status === 'paid'}
                            data-testid={`pay-entitlement-${ent.id}`}
                          >
                            {ent.status === 'paid' ? 'Paid' : 'Record payment'}
                          </Button>
                          {ent.status !== 'paid' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setHoldTarget(ent); setHoldReason(''); }}
                              data-testid={`hold-entitlement-${ent.id}`}
                            >
                              <Pause size={14} />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {current < 4 && entitlements.length === 0 && (
        <p className="text-xs text-foreground-muted inline-flex items-center gap-1.5">
          <Warning size={14} className="text-amber-500" weight="fill" />
          Per-investor amounts appear once entitlements are calculated.
        </p>
      )}

      {/* Payment dialog */}
      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
            <DialogDescription>
              {parties[payTarget?.investor_id] || payTarget?.investor_id} · entitled to {money(payTarget?.net_amount)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                className="mt-1"
                data-testid="pay-amount-input"
              />
            </div>
            <div>
              <Label className="text-xs">Payment reference</Label>
              <Input
                value={payForm.payment_reference}
                onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })}
                placeholder="e.g., SEPA-2026-0142"
                className="mt-1"
                data-testid="pay-reference-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>Cancel</Button>
            <Button onClick={submitPay} disabled={busy} data-testid="pay-confirm-btn">
              {busy ? 'Saving…' : 'Record payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold dialog */}
      <Dialog open={!!holdTarget} onOpenChange={(o) => !o && setHoldTarget(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="hold-dialog">
          <DialogHeader>
            <DialogTitle>Put payment on hold</DialogTitle>
            <DialogDescription>
              {parties[holdTarget?.investor_id] || holdTarget?.investor_id} will not be paid until the hold is released.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Reason</Label>
            <Input
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="e.g., Bank details pending verification"
              className="mt-1"
              data-testid="hold-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldTarget(null)}>Cancel</Button>
            <Button onClick={submitHold} disabled={busy} data-testid="hold-confirm-btn">
              {busy ? 'Saving…' : 'Put on hold'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this distribution?</DialogTitle>
            <DialogDescription>
              {dist.code} will be marked cancelled and its calculated entitlements removed. Only possible while
              nothing has been paid.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep it</Button>
            <Button
              onClick={async () => {
                setBusy(true);
                try {
                  await api.post(`/admin/v2/distributions/${distributionId}/cancel`, { reason: 'Cancelled by admin' });
                  toast.success('Distribution cancelled');
                  navigate('/admin/v2/distributions');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to cancel');
                } finally {
                  setBusy(false);
                  setCancelOpen(false);
                }
              }}
              disabled={busy}
              data-testid="cancel-confirm-btn"
            >
              {busy ? 'Cancelling…' : 'Cancel distribution'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDistributionWizardPage;
